// Section-level validation for step headers
// Location: src/features/imprint/composables/useSectionValidation.js

import { computed } from 'vue';
import { useImprintStore } from '../stores/useImprintStore';
import { useSelectionStore } from '../stores/useSelectionStore';
import { useNewImprintStore } from '../stores/useNewImprintStore';
import { useAppStore } from '../stores/useAppStore';
import { useDesignOnlineStore } from '@/features/design/stores/useDesignOnlineStore';
import { validateQuantityIncrement } from './useValidation';
import {getColorName} from '../static_data/sections'

/**
 * Validate Step 1: Colour & Quantity
 * 
 * WHAT IT VALIDATES:
 * 1. At least one colour must be selected
 *    - User must click on at least one colour card
 *    - Without colours, product cannot be ordered
 * 
 * 2. Quantities must be entered for selected colours
 *    - Each selected colour must have at least one size with quantity > 0
 *    - Example: If user selects "Red" colour, they must enter quantity for S, M, L, etc.
 * 
 * 3. Minimum order quantity must be met
 *    - Total quantity across all colours/sizes must be >= minOrderQty
 *    - Example: If minOrderQty = 18, user must order at least 18 items total
 *    - This is sum of ALL quantities: Red-S:5, Red-M:8, Blue-L:5 = 18 total
 * 
 * NOTE: For calendar mode, this validation is SKIPPED.
 * Calendar bundles don't have color/size selection.
 * Calendar validation is handled separately in calendarStore.
 * 
 * @param {Object} selectionStore - Selection store instance
 * @param {Object} imprintStore - Imprint store instance
 * @param {Object} appStore - App store instance (for checking calendar mode)
 * @returns {Object} { hasErrors, errorCount, errors }
 */
export function validateStep1(selectionStore, imprintStore, appStore = null) {
  const errors = [];
  
  // SKIP validation for calendar mode - no color/size selection
  if (appStore && appStore.isCalendarMode) {
    console.log('[Validation] Skipping Step 1 validation (calendar mode)');
    return {
      hasErrors: false,
      errorCount: 0,
      errors: [],
      incrementErrors: []
    };
  }
  
  const imprintKey = imprintStore.getImprintMethodKey;
  
  // Check if at least one color is selected
  const colorLines = selectionStore.getLinesByImprintKey(imprintKey);
  if (!colorLines || colorLines.length === 0) {
    errors.push(`Please select at least one ${getColorName(true)}`);
  }
  
  // Check if quantities are entered for selected colors
  // Use getTotalQtyByImprintKey which returns a number (sum of all quantities)
  const totalQty = selectionStore.getTotalQtyByImprintKey(imprintKey);
  if (totalQty === 0) {

    errors.push(`Please enter quantities for selected ${getColorName(true)}`);
  }
  
  // Check minimum quantity - get from configurable_array for current imprint method
  const sku = imprintStore.selectedImprintMethod?.sku;
  let minOrderQty = 0;
  
  if (sku && imprintStore.fullJson?.configurable_array) {
    const methodData = imprintStore.fullJson.configurable_array[sku];
    const parsedQty = parseInt(methodData?.min_order_qty, 10);
    minOrderQty = !isNaN(parsedQty) && parsedQty > 0 ? parsedQty : 0;
  }
  
  // Fallback to imprintStore.minOrderQty if not found in configurable_array
  if (minOrderQty === 0) {
    minOrderQty = imprintStore.minOrderQty || 0;
  }
  
  // Check minimum quantity - show remaining items needed
  if (minOrderQty > 0 && totalQty < minOrderQty) {
    const remaining = minOrderQty - totalQty;
    errors.push(`${remaining} more items until minimum quantity`);
  }
  
  // Check quantity increment validation (NEW)
  // Use currentQtyIncrement getter which reads from configurable_array[sku].qty_increment
  const qtyIncrement = imprintStore.currentQtyIncrement || 1;
  const maxQty = 10000;
  const incrementErrors = [];
  
  // Validate each color/size quantity against increment rules
  if (colorLines && colorLines.length > 0) {
    colorLines.forEach((line, lineIndex) => {
      if (line.selectedSizes) {
        Object.entries(line.selectedSizes).forEach(([size, qty]) => {
          if (qty > 0) {
            const validation = validateQuantityIncrement(qty, qtyIncrement, maxQty);
            if (!validation.isValid) {
              incrementErrors.push({
                lineIndex,
                size,
                qty,
                error: validation.error,
                suggestedQty: validation.suggestedQty
              });
            }
          }
        });
      }
    });
  }
  
  // Add increment errors to main errors list
  if (incrementErrors.length > 0) {
    // Group by error type for cleaner messages
    const errorsByType = {};
    incrementErrors.forEach(e => {
      if (!errorsByType[e.error]) {
        errorsByType[e.error] = [];
      }
      errorsByType[e.error].push(`${e.size}: ${e.qty}`);
    });
    
    // Add grouped error messages
    Object.entries(errorsByType).forEach(([errorMsg, sizes]) => {
      errors.push(`${errorMsg} (${sizes.join(', ')})`);
    });
  }
  
  return {
    hasErrors: errors.length > 0,
    errorCount: errors.length,
    errors,
    incrementErrors // Detailed errors for debugging/display
  };
}

/**
 * Validate Step 2: Customize Your Product
 * 
 * WHAT IT VALIDATES:
 * 
 * ARTWORK METHOD VALIDATION:
 * 1. Artwork method must be selected
 *    - User must click one of: "Start New Imprint", "Send Us Your Artwork", "Use Previous Artwork"
 *    - Cannot proceed without choosing how artwork will be provided
 * 
 * IMPRINT METHOD VALIDATION:
 * 2. Imprint method must be selected
 *    - User must select: Screen Print, DTG, Embroidery, or CMYK
 *    - Method determines pricing and capabilities
 * 
 * FOR "START NEW IMPRINT" (artworkMethod === 'new'):
 * For EACH imprint location (can have multiple imprints):
 * 
 * 3. Location validation:
 *    - User must select where imprint goes (e.g., "Front", "Back", "Left Chest")
 *    - Each imprint MUST have a location
 *    - Error example: "Imprint 1: Please select a location"
 * 
 * 4. Text lines validation:
 *    - For each text line (Line 1, Line 2, etc.):
 *      a) Text must be entered:
 *         - Cannot leave text field empty
 *         - Must contain at least one non-whitespace character
 *         - Error: "Imprint 1, Line 1: Please enter text"
 *      
 *      b) Colour must be selected:
 *         - Each text line needs a colour (e.g., "Black", "White", "Red")
 *         - Can be standard colour or custom Pantone
 *         - Error: "Imprint 1, Line 1: Please select a color"
 * 
 * 5. Image validation (if messageType is 'image' or 'text_image'):
 *    - If user wants to add image:
 *      a) Image must be uploaded:
 *         - File must be selected and uploaded successfully
 *         - Error: "Imprint 1: Please upload an image"
 *      
 *      b) Image colour must be selected:
 *         - Even images need a colour selection for printing
 *         - Error: "Imprint 1: Please select image color"
 * 
 * FOR "SEND US YOUR ARTWORK" (artworkMethod === 'send'):
 * 6. Location must be selected
 *    - Same as "Start New Imprint" - must choose location
 * 7. Special instructions can be provided (optional)
 * 
 * FOR "USE PREVIOUS ARTWORK" (artworkMethod === 'previous'):
 * 8. Previous artwork must be selected from list
 *    - User must choose which previously used artwork to reuse
 * 
 * VALIDATION EXAMPLES:
 * - "Please select an artwork method" → No radio button clicked
 * - "Please select an imprint method" → No Screen Print/DTG/etc selected
 * - "Imprint 1: Please select a location" → First imprint has no location
 * - "Imprint 1, Line 1: Please enter text" → Line 1 text field is empty
 * - "Imprint 1, Line 2: Please select a color" → Line 2 has no colour chosen
 * - "Imprint 2: Please upload an image" → Second imprint is image type but no file uploaded
 * 
 * @param {Object} imprintStore - Imprint store instance
 * @param {Object} newImprintStore - New imprint store instance
 * @returns {Object} { hasErrors, errorCount, errors }
 */
export function validateStep2(imprintStore, newImprintStore, appStore = null) {
  const errors = [];
  
  // ✅ ENVELOP PRODUCTS: Skip all Step 2 validation (no location, no imprint message)
  if (appStore && appStore.isEnvelopProduct) {
    console.log('[Validation] Skipping Step 2 validation (Envelop product)');
    return {
      hasErrors: false,
      errorCount: 0,
      errors: []
    };
  }
  
  // Check if artwork method is selected
  if (!imprintStore.selectedArtworkMethod) {
    errors.push('Please select an artwork method');
  }
  
  // Check if imprint method is selected
  if (!imprintStore.selectedImprintMethod || !imprintStore.selectedImprintMethod.sku) {
    errors.push('Please select an imprint method');
  }
  
  // Validate based on artwork method
  const artworkMethod = imprintStore.selectedArtworkMethod;
  
  if (artworkMethod === 'new') {
    // Validate "Start New Imprint" data
    newImprintStore.imprints.forEach((imprint, index) => {
      // Check location
      if (!imprint.selectedLocation || !imprint.selectedLocation.sku) {
        errors.push(`Imprint ${index + 1}: Please select a location`);
      }

  // Check text lines ONLY for text / text_image
  if (imprint.messageType === 'text' || imprint.messageType === 'text_image') {
      const messageLines = imprint.textLines.filter(line => line.type === 'message');
      messageLines.forEach((line, lineIndex) => {
          const hasText = line.message && line.message.trim().length > 0;
          const hasColor = !!line.selectedColor;

              // Line 1 required
            if (lineIndex === 0) {
              if (!hasText) {
                errors.push(`Imprint ${index + 1}, Line ${lineIndex + 1}: Please enter text`);
              }
              if (!hasColor) {
                errors.push(`Imprint ${index + 1}, Line ${lineIndex + 1}: Please select a ${getColorName(true)}`);
              }
            } else {
              // Lines 2-5 optional — validate only if partially filled
              // If text entered but no colour → colour is required
              if (hasText && !hasColor) {
                errors.push(`Imprint ${index + 1}, Line ${lineIndex + 1}: Please select a ${getColorName(true)}`);
              }
            }
        });
    }
      
      // Check image (if messageType is 'image' or 'text_image')
      console.log(`%c[VALIDATE-IMAGE] Imprint ${index + 1}`, 'color: purple; font-weight: bold', {
        messageType: imprint.messageType,
        hasImageData: !!imprint.imageData,
        imageData: imprint.imageData,
        activeMethodSku: imprint.activeMethodSku,
        textLines: imprint.textLines?.map(l => ({ id: l.id, type: l.type, hasColor: !!l.selectedColor })),
        methodsDataKeys: Object.keys(imprint.methodsData || {}),
        'methodsData[active].messageType': imprint.activeMethodSku ? imprint.methodsData[imprint.activeMethodSku]?.messageType : 'N/A',
        'methodsData[active].imageData': imprint.activeMethodSku ? (imprint.methodsData[imprint.activeMethodSku]?.imageData ? '✅' : '❌ NULL') : 'N/A',
      });
      if (imprint.messageType === 'image' || imprint.messageType === 'text_image') {
        if (!imprint.imageData) {
          console.error(`%c[VALIDATE-IMAGE] ❌ ERROR: messageType='${imprint.messageType}' but imageData=null!`, 'color: red; font-weight: bold');
          errors.push(`Imprint ${index + 1}: Please upload an image`);
        }
        
        // CRITICAL FIX: Image color REQUIRED only for Text & Image (NOT for Image Only)
        // Image Only doesn't need color selection (like old architecture)
        if (imprint.messageType === 'text_image' || imprint.messageType === 'image') {
          const imageLine = imprint.textLines.find(line => line.type === 'image');
          if (imageLine && !imageLine.selectedColor) {
            errors.push(`Imprint ${index + 1}: Please select image ${getColorName(true)}`);
          }
        }
      }
    });
  } else if (artworkMethod === 'send') {
    // Validate "Send Us Your Artwork" data
    // Location must be selected
    // (Add more validations as needed)
  } else if (artworkMethod === 'previous') {
    // Validate "Use Previous Artwork" data
    // Previous artwork must be selected
    // (Add more validations as needed)
  } else if (artworkMethod === 'design') {
    // ✅ NEW: Validate "Design Online" data
    const designOnlineStore = useDesignOnlineStore();
    
    // Check if design exists
    if (!designOnlineStore.hasDesign) {
      errors.push('Please create or upload a design using Design Online');
    }
    
    // ✅ CRITICAL: Check if user approved the design
    if (designOnlineStore.hasDesign && !designOnlineStore.isApproved) {
      errors.push('Please approve your design by checking the confirmation box');
    }
  }
  
  return {
    hasErrors: errors.length > 0,
    errorCount: errors.length,
    errors
  };
}

/**
 * Validate Step 3: Add-Ons (Optional)
 * 
 * WHAT IT VALIDATES:
 * - This step is OPTIONAL
 * - No required fields or selections
 * - User can skip this step entirely
 * 
 * POTENTIAL VALIDATIONS (when add-ons are implemented):
 * - If "Rush Production" selected → Validate rush date is in future
 * - If "Special Packaging" selected → Validate packaging type chosen
 * - If "Additional Services" selected → Validate service details provided
 * 
 * Currently returns no errors since step is optional.
 * 
 * @returns {Object} { hasErrors, errorCount, errors }
 */
export function validateStep3() {
  const errors = [];
  
  // Step 3 is optional, so no required validations
  // Add validations here if add-ons become required
  
  return {
    hasErrors: errors.length > 0,
    errorCount: errors.length,
    errors
  };
}

/**
 * Validate Step 4: Review & Add to Cart
 * 
 * WHAT IT VALIDATES:
 * This is a COMBINED validation that checks ALL previous steps.
 * 
 * It runs:
 * 1. validateStep1() → All Colour & Quantity checks (skipped for calendar mode)
 * 2. validateStep2() → All Customize Product checks  
 * 3. validateStep3() → All Add-Ons checks (currently none)
 * 
 * This ensures that before adding to cart:
 * - User has selected colours and entered quantities (default mode)
 * - User has met minimum order quantity
 * - User has chosen artwork and imprint methods
 * - User has provided all imprint details (text, colours, images, locations)
 * - All required fields across all steps are complete
 * 
 * The error list will show ALL problems from all steps, for example:
 * - "Please select at least one colour" (from Step 1, default mode only)
 * - "Please enter quantities for selected colours" (from Step 1, default mode only)
 * - "Minimum order quantity is 18" (from Step 1)
 * - "Please select an artwork method" (from Step 2)
 * - "Imprint 1: Please select a location" (from Step 2)
 * - "Imprint 1, Line 1: Please enter text" (from Step 2)
 * 
 * User must fix ALL errors before "Add to Cart" button becomes enabled.
 * 
 * Combines all previous validations
 * @param {Object} selectionStore - Selection store instance
 * @param {Object} imprintStore - Imprint store instance
 * @param {Object} newImprintStore - New imprint store instance
 * @param {Object} appStore - App store instance (for checking calendar mode)
 * @returns {Object} { hasErrors, errorCount, errors }
 */
export function validateStep4(selectionStore, imprintStore, newImprintStore, appStore = null) {
  const step1 = validateStep1(selectionStore, imprintStore, appStore);
  const step2 = validateStep2(imprintStore, newImprintStore, appStore);
  const step3 = validateStep3();
  
  const allErrors = [
    ...step1.errors,
    ...step2.errors,
    ...step3.errors
  ];
  
  return {
    hasErrors: allErrors.length > 0,
    errorCount: allErrors.length,
    errors: allErrors
  };
}

/**
 * Composable for section validation in components
 * @returns {Object} Validation utilities and computed properties
 */
export function useSectionValidation() {
  const imprintStore = useImprintStore();
  const selectionStore = useSelectionStore();
  const newImprintStore = useNewImprintStore();
  const appStore = useAppStore();
  
  // Step 1 validation
  const step1Validation = computed(() => {
    return validateStep1(selectionStore, imprintStore, appStore);
  });
  
  // Step 2 validation
  const step2Validation = computed(() => {
    return validateStep2(imprintStore, newImprintStore, appStore);
  });
  
  // Step 3 validation
  const step3Validation = computed(() => {
    return validateStep3();
  });
  
  // Step 4 validation
  const step4Validation = computed(() => {
    return validateStep4(selectionStore, imprintStore, newImprintStore, appStore);
  });
  
  // Get validation for specific section
  const getSectionValidation = (sectionId) => {
    switch (sectionId) {
      case 'colourQuantity':
        return step1Validation.value;
      case 'imprintSelection':
        return step2Validation.value;
      case 'addons':
        return step3Validation.value;
      case 'review':
        return step4Validation.value;
      default:
        return { hasErrors: false, errorCount: 0, errors: [] };
    }
  };
  
  // Check if section has errors
  const sectionHasErrors = (sectionId) => {
    const validation = getSectionValidation(sectionId);
    return validation.hasErrors;
  };
  
  // Get error count for section
  const getSectionErrorCount = (sectionId) => {
    const validation = getSectionValidation(sectionId);
    return validation.errorCount;
  };
  
  // Get errors for section
  const getSectionErrors = (sectionId) => {
    const validation = getSectionValidation(sectionId);
    return validation.errors;
  };
  
  return {
    step1Validation,
    step2Validation,
    step3Validation,
    step4Validation,
    getSectionValidation,
    sectionHasErrors,
    getSectionErrorCount,
    getSectionErrors
  };
}
